import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Loader2,
  RefreshCcw,
  Search,
  Download,
  Wallet,
  AlertCircle,
  CheckCircle2,
  Clock,
  DollarSign,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending", color: "amber" },
  { id: "unpaid", label: "Unpaid", color: "rose" },
  { id: "paid", label: "Paid", color: "emerald" },
];

function csvEscape(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
}

function ageHuman(sec) {
  if (sec === null || sec === undefined) return "—";
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  return `${Math.floor(sec / 86400)}d`;
}

export default function AdminTermPayments() {
  const [terms, setTerms] = useState([]);
  const [termId, setTermId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/payments-hub/admin/term-payment-status/terms`, {
          withCredentials: true,
        });
        setTerms(res.data.terms || []);
        if (res.data.terms?.length && !termId) {
          // Default = newest non-hidden term, else newest overall
          const nonHidden = res.data.terms.find((t) => !t.is_hidden);
          setTermId((nonHidden || res.data.terms[0]).id);
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load terms");
      }
    })();
  }, []); // eslint-disable-line

  const load = async () => {
    if (!termId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/payments-hub/admin/term-payment-status`, {
        params: { term_id: termId, status_filter: statusFilter, q: search || undefined },
        withCredentials: true,
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load term payment status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [termId, statusFilter]); // eslint-disable-line

  const summary = data?.summary;
  const rows = data?.rows || [];

  const exportCsv = () => {
    if (!rows.length) {
      toast.info("No rows to export");
      return;
    }
    const headers = [
      "Registration ID",
      "Parent",
      "Email",
      "Phone",
      "SCC Member",
      "Children",
      "Children Count",
      "Status",
      "Invoiced",
      "Received",
      "Balance",
      "Method",
      "Last Activity (h)",
      "Duplicates",
      "Paid At",
      "Created At",
    ];
    const lines = [headers.map(csvEscape).join(",")];
    for (const r of rows) {
      lines.push([
        r.registration_id,
        r.parent_name,
        r.parent_email,
        r.parent_phone,
        r.is_scc_member ? "Yes" : "No",
        (r.children || []).map((c) => `${c.name} (${c.session_count})`).join(" | "),
        r.children_count,
        r.status,
        r.amount_invoiced?.toFixed(2),
        r.amount_received?.toFixed(2),
        r.balance?.toFixed(2),
        r.current_txn_method || "—",
        r.current_txn_age_seconds != null ? (r.current_txn_age_seconds / 3600).toFixed(1) : "",
        r.duplicate_pending_count > 0 ? r.duplicate_pending_count : "",
        r.paid_at || "",
        r.created_at || "",
      ].map(csvEscape).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `term-payments-${data?.term?.name || termId}-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  const selectedTerm = useMemo(() => terms.find((t) => t.id === termId), [terms, termId]);

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      <header className="bg-slate-950 border-b border-white/5">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button variant="ghost" className="text-white" data-testid="back-btn">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
            </Link>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Wallet className="w-6 h-6 text-emerald-400" />
              Term Payments
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={load} variant="outline" size="sm" disabled={loading} data-testid="refresh-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
              Refresh
            </Button>
            <Button onClick={exportCsv} variant="outline" size="sm" className="border-emerald-500/40 text-emerald-300" data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Term picker + filters */}
        <Card className="p-4 mb-4 bg-slate-900/50">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-4">
              <label className="block text-xs text-slate-400 mb-1">Term</label>
              <select
                value={termId}
                onChange={(e) => setTermId(e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-white text-sm"
                data-testid="term-select"
              >
                {terms.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                    {t.is_hidden ? " (hidden)" : ""}
                    {t.is_finalized ? " [finalized]" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-5">
              <label className="block text-xs text-slate-400 mb-1">Search parent or child</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && load()}
                  placeholder="e.g. Olbourne or Ben"
                  className="pl-9 bg-slate-800 border-slate-700 text-sm text-white"
                  data-testid="search-input"
                />
              </div>
            </div>
            <div className="md:col-span-3 flex gap-1 flex-wrap">
              {STATUS_FILTERS.map((f) => {
                const isActive = statusFilter === f.id;
                return (
                  <Button
                    key={f.id}
                    variant={isActive ? "default" : "outline"}
                    size="sm"
                    onClick={() => setStatusFilter(f.id)}
                    className={isActive ? "" : "border-slate-700 text-slate-300"}
                    data-testid={`filter-${f.id}-btn`}
                  >
                    {f.label}
                  </Button>
                );
              })}
            </div>
          </div>
        </Card>

        {/* Summary cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="summary-cards">
            <SummaryCard
              icon={<DollarSign className="w-5 h-5 text-blue-400" />}
              label="Registrations"
              value={summary.total_registrations}
              testid="sum-total"
            />
            <SummaryCard
              icon={<CheckCircle2 className="w-5 h-5 text-emerald-400" />}
              label="Paid"
              value={summary.paid}
              accent="emerald"
              testid="sum-paid"
            />
            <SummaryCard
              icon={<Clock className="w-5 h-5 text-amber-400" />}
              label="Pending"
              value={summary.pending}
              accent="amber"
              testid="sum-pending"
            />
            <SummaryCard
              icon={<AlertCircle className="w-5 h-5 text-rose-400" />}
              label="Unpaid"
              value={summary.unpaid}
              accent="rose"
              testid="sum-unpaid"
            />
            <SummaryCard
              icon={<Wallet className="w-5 h-5 text-purple-400" />}
              label="Balance Outstanding"
              value={`$${summary.amount_pending?.toFixed(2)}`}
              testid="sum-balance"
            />
          </div>
        )}

        {/* Rows table */}
        <Card className="bg-slate-900/50 overflow-hidden">
          {loading ? (
            <div className="py-16 flex justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-sm">
              No registrations match your filters for{" "}
              <span className="text-white font-medium">{selectedTerm?.name}</span>.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payments-table">
                <thead className="bg-slate-800/60 text-slate-300">
                  <tr>
                    <th className="text-left p-3">Parent</th>
                    <th className="text-left p-3">Children</th>
                    <th className="text-right p-3">Invoiced</th>
                    <th className="text-right p-3">Received</th>
                    <th className="text-right p-3">Balance</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Method</th>
                    <th className="text-right p-3">Age</th>
                    <th className="text-left p-3">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <RowItem key={r.registration_id} row={r} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function SummaryCard({ icon, label, value, accent, testid }) {
  const accentClass = {
    emerald: "border-emerald-500/30 bg-emerald-500/5",
    amber: "border-amber-500/30 bg-amber-500/5",
    rose: "border-rose-500/30 bg-rose-500/5",
  }[accent] || "border-slate-700 bg-slate-800/30";
  return (
    <Card className={`p-4 ${accentClass}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-slate-400 uppercase tracking-wider">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-bold text-white mt-1">{value}</div>
    </Card>
  );
}

function RowItem({ row }) {
  const statusColor = {
    paid: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    pending: "bg-amber-500/20 text-amber-300 border-amber-500/30",
    unpaid: "bg-rose-500/20 text-rose-300 border-rose-500/30",
  }[row.status];
  return (
    <tr className="border-t border-slate-800 hover:bg-slate-800/30">
      <td className="p-3">
        <div className="font-medium text-white">{row.parent_name || "—"}</div>
        <div className="text-xs text-slate-400">{row.parent_email}</div>
        {row.is_scc_member && (
          <Badge variant="outline" className="border-emerald-500/30 text-emerald-300 mt-1 text-[10px]">
            SCC
          </Badge>
        )}
      </td>
      <td className="p-3 text-slate-300">
        <div className="space-y-0.5">
          {row.children.slice(0, 3).map((c, i) => (
            <div key={i} className="text-xs">
              {c.name}
              <span className="text-slate-500 ml-1">({c.session_count})</span>
              {c.is_existing && <span className="ml-1 text-blue-300">[re]</span>}
            </div>
          ))}
          {row.children.length > 3 && (
            <div className="text-xs text-slate-500">+ {row.children.length - 3} more</div>
          )}
        </div>
      </td>
      <td className="p-3 text-right text-white">${row.amount_invoiced.toFixed(2)}</td>
      <td className="p-3 text-right text-emerald-300">${row.amount_received.toFixed(2)}</td>
      <td className={`p-3 text-right font-semibold ${row.balance > 0 ? "text-rose-300" : "text-slate-300"}`}>
        ${row.balance.toFixed(2)}
      </td>
      <td className="p-3">
        <Badge className={`${statusColor} border`}>{row.status}</Badge>
        {row.registration_status && (
          <div className="text-[10px] text-slate-500 mt-0.5">reg: {row.registration_status}</div>
        )}
      </td>
      <td className="p-3 text-slate-300 text-xs capitalize">{row.current_txn_method || "—"}</td>
      <td className="p-3 text-right text-slate-400 text-xs">{ageHuman(row.current_txn_age_seconds)}</td>
      <td className="p-3 text-xs">
        {row.duplicate_pending_count > 0 && (
          <Badge variant="outline" className="border-rose-500/40 text-rose-300" title="Duplicate open pending transactions for this parent — admin should clean up">
            {row.duplicate_pending_count} dup{row.duplicate_pending_count > 1 ? "s" : ""}
          </Badge>
        )}
        {row.current_txn_id && (
          <Link to={`/admin/payments?txn=${row.current_txn_id}`} className="text-blue-400 hover:underline ml-2">
            Open
          </Link>
        )}
      </td>
    </tr>
  );
}
